import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, Sparkles, UserRound } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";

const LINKS = [
  { id: "#garage", label: "Le Garage", testId: "nav-link-presentation" },
  { id: "#services", label: "Services", testId: "nav-link-services" },
  { id: "#vehicules", label: "Véhicules", testId: "nav-link-vehicules" },
  { id: "#avis", label: "Avis", testId: "nav-link-avis" },
  { id: "#horaires", label: "Horaires & Accès", testId: "nav-link-horaires" },
  { id: "#contact", label: "Contact", testId: "nav-link-contact" },
];

export const Navbar = ({ onNavigate }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [promoVisible, setPromoVisible] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    onNavigate(id);
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled ? "bg-[#07090E]/85 backdrop-blur-xl border-b border-white/10" : "bg-transparent border-b border-transparent"
      }`}
    >
      <AnimatePresence>
        {promoVisible && (
          <motion.div
            data-testid="promo-bar"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="bg-gradient-to-r from-[#F59E0B] to-[#FBBF24] text-[#07090E] overflow-hidden"
          >
            <div className="max-w-7xl mx-auto px-6 lg:px-10 py-2 flex items-center justify-center gap-2 md:gap-3 text-xs md:text-sm font-semibold">
              <Sparkles className="w-4 h-4 shrink-0" />
              <span className="truncate md:whitespace-normal">
                Offre du moment : <strong>200€ offerts</strong> pour tout changement de pare-brise
              </span>
              <button
                data-testid="promo-bar-cta"
                onClick={() => go("#contact")}
                className="underline underline-offset-2 hover:no-underline whitespace-nowrap"
              >
                En profiter
              </button>
              <button
                data-testid="promo-bar-close"
                onClick={() => setPromoVisible(false)}
                aria-label="Fermer l'annonce"
                className="ml-1 opacity-70 hover:opacity-100 shrink-0"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 md:h-20 flex items-center justify-between">
        <button
          data-testid="nav-link-home"
          onClick={() => go("#accueil")}
          className="flex items-center gap-3 group"
        >
          <span className="rounded-md bg-white px-2 py-1 flex items-center shadow-[0_0_18px_rgba(0,102,255,0.25)] transition-transform duration-300 group-hover:-translate-y-0.5">
            <img src="/logo-emblem.png" alt="Logo AMP Services 01" className="h-8 w-auto" />
          </span>
          <span className="text-left leading-none">
            <span className="block font-display font-extrabold tracking-tight text-sm md:text-base uppercase whitespace-nowrap">
              Auto Meca Pro
            </span>
            <span className="block font-mono-tech text-[10px] tracking-[0.3em] text-[#00E5FF] uppercase mt-1 whitespace-nowrap">
              AMP Services 01
            </span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-6">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={l.testId}
              onClick={() => go(l.id)}
              className="text-sm whitespace-nowrap text-slate-300 hover:text-white transition-colors duration-300 relative after:absolute after:-bottom-1 after:left-0 after:h-px after:w-0 after:bg-[#0066FF] after:transition-[width] after:duration-300 hover:after:w-full"
            >
              {l.label}
            </button>
          ))}
          <a
            data-testid="nav-call-button"
            href="tel:+33426612733"
            className="hidden xl:flex items-center gap-2 text-sm font-semibold whitespace-nowrap text-white hover:text-[#00E5FF] transition-colors duration-300"
          >
            <Phone className="w-4 h-4 text-[#00E5FF]" />
            04 26 61 27 33
          </a>
          <button
            data-testid="nav-cta-devis"
            onClick={() => go("#contact")}
            className="rounded-full bg-[#0066FF] hover:bg-[#0052CC] text-white text-sm font-semibold px-5 py-2.5 whitespace-nowrap transition-[background-color,box-shadow,transform] duration-300 hover:shadow-[0_0_24px_rgba(0,102,255,0.5)] hover:-translate-y-0.5"
          >
            Devis gratuit
          </button>
          <Link
            data-testid="nav-account-link"
            to="/compte"
            title={user ? `Bonjour ${user.name}` : "Mon compte"}
            className={`w-10 h-10 rounded-full border flex items-center justify-center transition-[border-color,background-color,color] duration-300 ${
              user
                ? "border-[#0066FF] bg-[#0066FF]/15 text-[#00E5FF]"
                : "border-white/15 text-slate-300 hover:border-[#0066FF] hover:text-white"
            }`}
          >
            <UserRound className="w-5 h-5" />
          </Link>
        </nav>

        <button
          data-testid="nav-mobile-menu-button"
          className="lg:hidden text-white"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-[#07090E]/95 backdrop-blur-xl border-b border-white/10 overflow-hidden"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`${l.testId}-mobile`}
                  onClick={() => go(l.id)}
                  className="text-left text-lg font-display font-bold uppercase tracking-tight text-slate-200"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="nav-cta-devis-mobile"
                onClick={() => go("#contact")}
                className="rounded-full bg-[#0066FF] text-white font-semibold px-5 py-3 w-fit"
              >
                Devis gratuit
              </button>
              <Link
                data-testid="nav-account-link-mobile"
                to="/compte"
                onClick={() => setOpen(false)}
                className="flex items-center gap-2 text-sm font-semibold text-slate-300 hover:text-white transition-colors duration-300 mt-1"
              >
                <UserRound className="w-4 h-4 text-[#00E5FF]" />
                {user ? `Mon espace (${user.name})` : "Mon compte"}
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
